import { createContext, Dispatch, ReactNode, useCallback, useContext, useMemo, useReducer } from "react";
import { ChatContextState, chatReducer, ChatReducerAction, initialState } from "./reducer";
import { Conversation, nebulaChatApi } from "../../redux/services/nebulaChat";
import { useTypedDispatch, useTypedSelector } from "../../redux/hooks";
import { selectCurrentUserId, updateFavouriteChat, updateMutedChat } from "../../redux/slices";

interface Props {
    reducer: Dispatch<ChatReducerAction>;
    state: ChatContextState;
    conversation: Conversation;
    updateMember: (val: { is_muted?: boolean, is_favourite?: boolean, is_archived?: boolean }) => void;
}

const ConversationContext = createContext<Props | null>(null)

export const useConversationContext = () => {
    const context = useContext(ConversationContext);

    if (!context?.conversation) {
        throw new Error(
            'Component requiring chat context rendered without a chat'
        )
    }

    const chatState: ChatContextState = context.state
    const useChatAction: (val: ChatReducerAction) => void = context.reducer

    return {
        conversation: context.conversation,
        updateMember: context.updateMember,
        expandMessage: chatState.expandMessage,
        files: chatState.filesToUpload,
        sideBarVisible: chatState.sidebarVisible,
        isScrolled: chatState.isScrolled,
        chatState,
        useChatAction,
    }
}

interface ProviderProps {
    children: ReactNode;
    conversation: Conversation;
}

const ConversationProvider: React.FC<ProviderProps> = ({ children, conversation }) => {
    const userUuid = useTypedSelector(selectCurrentUserId);

    const [state, dispatch] = useReducer(chatReducer, initialState)

    const [updateMemberCall, { isLoading }] = nebulaChatApi.endpoints.updateChatMember.useMutation();
    const storeDispatch = useTypedDispatch();

    const updateMember = useCallback(({ is_muted, is_favourite, is_archived }: { is_muted?: boolean, is_favourite?: boolean, is_archived?: boolean }) => {
        if (isLoading) {
            return;
        }

        if (typeof is_favourite === 'boolean') {
            storeDispatch(updateFavouriteChat(conversation.uuid, is_favourite));
        }

        if (typeof is_muted === 'boolean') {
            storeDispatch(
                updateMutedChat({
                    userUuid,
                    isChannel: conversation.type === 'channel',
                    uuidtoMute: conversation.uuid,
                    mute: is_muted
                })
            );
        }

        updateMemberCall({
            to: conversation.uuid,
            to_type: conversation.type,
            is_muted: is_muted ?? conversation.is_muted,
            is_favourite: is_favourite ?? conversation.is_favourite,
            is_archived: is_archived ?? conversation.is_archived
        })
    }, [conversation, isLoading])

    const contextValue = useMemo(
        () => ({ reducer: dispatch, state, conversation, updateMember }),
        [state, conversation, updateMember]
    );

    return (
        <ConversationContext.Provider value={contextValue}>
            {children}
        </ConversationContext.Provider>
    )
}

export default ConversationProvider;
